import { useState, useEffect, useCallback } from 'react'

interface LightboxImage {
  id: string | number
  signedUrl: string
  legenda?: string | null
}

interface FotoLightboxProps {
  images: LightboxImage[]
  initialIndex?: number
  onClose: () => void
}

/**
 * Lightbox fullscreen para fotos/croquis de sessão.
 */
export default function FotoLightbox({ images, initialIndex = 0, onClose }: FotoLightboxProps) {
  const [idx, setIdx] = useState<number>(initialIndex)
  const img = images[idx]

  const prev = useCallback(() => setIdx(i => (i > 0 ? i - 1 : images.length - 1)), [images.length])
  const next = useCallback(() => setIdx(i => (i < images.length - 1 ? i + 1 : 0)), [images.length])

  // Keyboard nav
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, prev, next])

  if (!img) return null

  return (
    <div className="fixed inset-0 z-[350] flex flex-col items-center justify-center bg-black/[0.92]">

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10">
        <span className="text-[12px] text-[#888]">
          {idx + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full border-none cursor-pointer bg-white/10"
        >
          <span className="material-symbols-outlined text-[#ccc] text-xl">close</span>
        </button>
      </div>

      {/* Image */}
      <img
        src={img.signedUrl}
        alt={img.legenda ?? 'Foto'}
        className="max-w-full max-h-[80vh] object-contain select-none"
        style={{ touchAction: 'pinch-zoom' }}
        draggable={false}
      />

      {/* Legenda */}
      {img.legenda && (
        <p className="text-[13px] text-[#aaa] mt-3 px-6 text-center max-w-md">{img.legenda}</p>
      )}

      {/* Nav arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border-none cursor-pointer bg-white/[0.08]"
          >
            <span className="material-symbols-outlined text-[#ccc] text-[22px]">chevron_left</span>
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border-none cursor-pointer bg-white/[0.08]"
          >
            <span className="material-symbols-outlined text-[#ccc] text-[22px]">chevron_right</span>
          </button>
        </>
      )}
    </div>
  )
}
